import React from "react";
import { AdvancedSortDef, SortParam } from "./advanced-filter-types";
import { Button } from "@/ui-components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/ui-components/ui/select";
import { Trash2, ArrowDownAZ, ArrowUpAZ } from "lucide-react";

interface AdvancedSortRowProps {
  sort: SortParam;
  sortDefs: AdvancedSortDef[];
  onKeyChange: (newKey: string) => void;
  onDirectionChange: (newDirection: "asc" | "desc") => void;
  onRemove: () => void;
  availableKeys: string[]; // Keys not yet used (plus current one)
  disableRemove?: boolean;
}

export function AdvancedSortRow({
  sort,
  sortDefs,
  onKeyChange,
  onDirectionChange,
  onRemove,
  availableKeys,
  disableRemove
}: AdvancedSortRowProps) {
  return (
    <div className="group flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full p-3 sm:p-2 rounded-xl bg-gray-50/50 border border-gray-100 transition-colors hover:border-gray-200 hover:bg-gray-50 shadow-sm sm:shadow-none">
      {/* Attribute Selector & Mobile Remove Button */}
      <div className="flex items-center gap-2 w-full sm:flex-1 sm:min-w-[120px]">
        <div className="flex-1 min-w-0">
          <Select
            value={sort.key}
            onValueChange={(val) => {
              onKeyChange(val);
            }}
          >
            <SelectTrigger className="h-9 text-xs font-medium bg-white border-gray-200 rounded-lg hover:border-gray-300 transition-colors shadow-none focus:ring-1 focus:ring-blue-500/20">
              <SelectValue placeholder="Sortieren nach..." />
            </SelectTrigger>
            <SelectContent className="rounded-xl border-gray-200 shadow-lg">
              {sortDefs.map((def) => (
                <SelectItem
                  key={def.key}
                  value={def.key}
                  disabled={!availableKeys.includes(def.key) && def.key !== sort.key}
                  className="text-xs rounded-md"
                >
                  <div className="flex items-center gap-2">
                    {def.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Mobile Remove Button */}
        {!disableRemove && (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="sm:hidden h-9 w-9 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all flex-shrink-0 border border-transparent hover:border-red-100"
            title="Sortierung entfernen"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      {/* Direction Toggle */}
      <div className="w-full sm:w-[130px] shrink-0">
        <Select
          value={sort.direction}
          onValueChange={(val: 'asc' | 'desc') => {
            onDirectionChange(val);
          }}
        >
          <SelectTrigger className="h-9 text-xs font-medium bg-white border-gray-200 rounded-lg hover:border-gray-300 transition-colors shadow-none focus:ring-1 focus:ring-blue-500/20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-xl border-gray-200 shadow-lg">
            <SelectItem value="asc" className="text-xs rounded-md">
              <div className="flex items-center gap-2">
                <ArrowDownAZ className="h-3.5 w-3.5 text-gray-400" />
                <span>Aufsteigend</span>
              </div>
            </SelectItem>
            <SelectItem value="desc" className="text-xs rounded-md">
              <div className="flex items-center gap-2">
                <ArrowUpAZ className="h-3.5 w-3.5 text-gray-400" />
                <span>Absteigend</span>
              </div>
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Desktop Remove Button */}
      <div className="hidden sm:block w-8 shrink-0">
        {!disableRemove ? (
          <Button
            variant="ghost"
            size="icon"
            onClick={onRemove}
            className="h-8 w-8 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
            title="Sortierung entfernen"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ) : (
          <div className="h-8 w-8" />
        )}
      </div>
    </div>
  );
}
